import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading } from "../components/Layout";
import { BandBadge, StatusChip } from "../components/Badges";
import LeadScoreGauge from "../components/LeadScoreGauge";
import FirstOutreachCard from "../components/FirstOutreachCard";
import MeetingBookingPanel from "../components/MeetingBookingPanel";
import VoicePanel from "../components/VoicePanel";
import VoiceMemo from "../components/VoiceMemo";
import MeetingsPanel from "../components/MeetingsPanel";
import WhatsAppPanel from "../components/WhatsAppPanel";
import ProposalPanel from "../components/ProposalPanel";
import LifecycleTimeline from "../components/LifecycleTimeline";
import NextActionCard from "../components/NextActionCard";
import CloseoutPanel from "../components/CloseoutPanel";
import CompanyIntel from "../components/CompanyIntel";
import { STAGES } from "../lib/constants";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Mail, MessageCircle, Zap, RotateCcw, Phone, Linkedin, ExternalLink,
  Send, CalendarClock, Trash2, Loader2,
} from "lucide-react";

function Bar({ label, value, max, color }) {
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-[#1C2530]/80">{label}</span>
        <span className="font-mono tabular-nums text-[#1C2530]/60">{value} / {max}</span>
      </div>
      <div className="h-2 bg-[#F5F3EE] border border-[#DFDAD0]">
        <div className="h-full" style={{ width: `${(value / max) * 100}%`, background: color }} />
      </div>
    </div>
  );
}

function Action({ icon: Icon, label, onClick, testid, disabled, spin }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} aria-busy={spin || undefined}
      className="flex items-center gap-2 border border-[#DFDAD0] bg-white px-3 py-2 text-xs font-mono uppercase tracking-wider hover:bg-[#EFECE5] transition-colors disabled:opacity-40">
      <Icon className={`w-3.5 h-3.5 ${spin ? "animate-spin" : ""}`} strokeWidth={1.5} /> {label}
    </button>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [mrefresh, setMrefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const [briefBusy, setBriefBusy] = useState(false);
  const [briefFlash, setBriefFlash] = useState(false);
  const [briefError, setBriefError] = useState("");
  const [briefElapsed, setBriefElapsed] = useState(0);
  const briefRef = useRef(null);
  const [note, setNote] = useState("");
  const [nurtureDate, setNurtureDate] = useState("");
  const [suggested, setSuggested] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("reachout");

  const load = () => api.get(`/leads/${id}`).then((r) => { setLead(r.data); setErr(null); }).catch((e) => setErr(e.response?.status === 403 ? "forbidden" : "notfound"));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get(`/leads/${id}/suggested-contacts`).then((r) => setSuggested(r.data)).catch(() => {}); }, [id]);
  // Auto-generate when a lead has no brief yet, and resume polling if a job is already running (e.g. after navigating back).
  // NOTE: This hook must run on every render (before any early returns) to satisfy react-hooks/rules-of-hooks.
  useEffect(() => {
    if (!lead || briefBusy) return;
    const job = lead.brief_job || {};
    if (job.status === "running") {
      setBriefBusy(true); setBriefError("");
      pollBrief(new Date(job.started_at || Date.now()).getTime(), !lead.fit_brief, true).catch(() => {}).finally(() => setBriefBusy(false));
    } else if (!lead.fit_brief && job.status !== "error" && user?.role !== "super_admin") {
      brief(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, lead?.brief_job?.status]);

  if (err) return (
    <>
      <PageHeader eyebrow="LEAD" title={err === "forbidden" ? "You don't have access to this lead" : "Lead not found"} />
      <div className="p-8">
        <button onClick={() => nav(-1)} className="flex items-center gap-1 font-mono text-xs text-[#1C2530]/60 hover:text-[#1C2530] mb-4"><ArrowLeft className="w-3.5 h-3.5" /> BACK</button>
        <div data-testid="lead-access-message" className="max-w-lg border border-[#DFDAD0] bg-white p-6 space-y-4">
          <div className="text-sm text-[#1C2530] leading-relaxed">
            {err === "forbidden"
              ? "This lead is assigned to another rep in your workspace, so it isn't in your queue. If it should be yours, ask your Sales Lead or Admin to reassign it — then this link will open for you."
              : "This lead no longer exists — it may have been removed, or the link points to a different workspace."}
          </div>
          <div className="flex gap-2">
            <button onClick={() => nav("/pipeline")} data-testid="lead-access-pipeline" className="border border-[#DFDAD0] px-3 py-1.5 text-sm hover:bg-[#EFECE5]">Go to Pipeline</button>
            {user.role === "rep" && <button onClick={() => nav("/queue")} data-testid="lead-access-queue" className="border border-[#DFDAD0] px-3 py-1.5 text-sm hover:bg-[#EFECE5]">My Queue</button>}
          </div>
        </div>
      </div>
    </>
  );
  if (!lead) return (<><PageHeader eyebrow="LEAD" title="Loading…" /><div className="p-8"><Loading /></div></>);

  const wrap = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const engagement = (event) => wrap(() => api.post(`/leads/${id}/engagement`, { event }), `Recorded: ${event}`);
  const pollBrief = async (startedAt, wasEmpty, quiet) => {
    let status = "running";
    while (status === "running") {
      await new Promise((r) => setTimeout(r, 2000));
      setBriefElapsed(Math.round((Date.now() - startedAt) / 1000));
      const { data } = await api.get(`/leads/${id}/brief/status`);
      status = data.status;
      if (status === "done") {
        await load();
        if (!quiet) toast.success(wasEmpty && data.stage === "Researched" ? "Fit brief generated · stage advanced to Researched" : "Fit brief generated");
        setBriefFlash(true); setTimeout(() => setBriefFlash(false), 2500);
      } else if (status === "error") {
        setBriefError(data.error || "Brief generation failed"); if (!quiet) toast.error(data.error || "Brief generation failed");
      } else if (status !== "running") {
        setBriefError("Brief generation did not start — please try again");
      }
    }
  };
  const brief = async (quiet = false) => {
    setBriefBusy(true); setBriefError(""); setBriefElapsed(0);
    if (!quiet) { toast.info("Generating fit brief — this can take up to a minute"); briefRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }
    const wasEmpty = !lead?.fit_brief;
    try {
      const { data } = await api.post(`/leads/${id}/brief`);
      await pollBrief(data.started_at ? new Date(data.started_at).getTime() : Date.now(), wasEmpty, quiet);
    } catch (e) { const msg = e.response?.data?.detail || e.message || "Brief generation failed"; setBriefError(typeof msg === "string" ? msg : JSON.stringify(msg)); if (!quiet) toast.error(typeof msg === "string" ? msg : "Brief generation failed"); }
    finally { setBriefBusy(false); }
  };
  const setStage = (stage) => wrap(() => api.post(`/leads/${id}/stage`, { stage }), `Stage → ${stage}`);
  const extendStage = () => { const reason = window.prompt("Reason for extending this stage (required):"); if (!reason) return; wrap(async () => { const r = await api.post(`/leads/${id}/stage/extend`, { reason }); if (r.data?.pending_approval) toast.info(r.data.message); else toast.success("Stage extended"); }); };
  const reinstate = () => wrap(() => api.post(`/leads/${id}/reinstate`), "Lead reinstated");
  const deleteLead = async () => {
    const name = lead?.account?.name || lead?.contact?.name || "this lead";
    if (window.prompt(`Permanently delete ${name} and its activity/outreach/email history? Type DELETE to confirm.`) !== "DELETE") return;
    setBusy(true);
    try { await api.delete(`/leads/${id}`); toast.success("Lead deleted"); nav(-1); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); setBusy(false); }
  };
  const setNurtureFn = () => { if (!nurtureDate) { toast.error("Pick a re-engage date"); return; } wrap(async () => { await api.post(`/leads/${id}/nurture`, { date: nurtureDate }); setNurtureDate(""); }, "Moved to Nurture — check back later"); };
  const addNote = () => { if (!note.trim()) return; wrap(async () => { await api.post(`/leads/${id}/note`, { text: note }); setNote(""); }, "Note added"); };

  const ev = lead.engagement_events || {};
  const c = lead.contact || {};
  const waNum = (c.whatsapp || c.phone || "").replace(/[^0-9]/g, "");
  const liUrl = c.linkedin_url || `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent((c.name || "") + " " + (lead.account?.name || ""))}`;
  const DeepLink = ({ href, disabled, icon: Ic, label, testid }) => (
    <a href={disabled ? undefined : href} target="_blank" rel="noreferrer" data-testid={testid} aria-disabled={disabled}
      title={disabled ? `${label} unavailable — no detail on file` : label}
      className={`flex items-center gap-1.5 border px-2.5 py-1.5 font-mono text-[10px] uppercase transition-colors ${disabled ? "border-[#DFDAD0] text-[#1C2530]/30 pointer-events-none" : "border-[#3E8F99] text-[#3E8F99] hover:bg-[#3E8F99]/10"}`}>
      <Ic className="w-3.5 h-3.5" /> {label}
    </a>
  );

  const TABS = [
    ["reachout", "Reach out"], ["meetings", "Meetings"], ["proposal", "Proposal"], ["timeline", "Timeline"],
    ...(user.role === "admin" ? [["admin", "Admin"]] : []),
  ];
  const goTab = (t) => { if (t === "context") { document.getElementById("lead-context")?.scrollIntoView({ behavior: "smooth" }); return; } setTab(t); document.getElementById("work-area")?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const bump = () => { load(); setMrefresh((x) => x + 1); };

  return (
    <>
      <PageHeader eyebrow={`${lead.account?.industry} · ${lead.region}`} title={lead.account?.name}
        right={<div className="flex items-center gap-3"><BandBadge band={lead.band} score={lead.total_score} /><StatusChip status={lead.status} /></div>} />
      <div className="p-8 space-y-6">
        <button onClick={() => nav(-1)} className="flex items-center gap-1 font-mono text-xs text-[#1C2530]/60 hover:text-[#1C2530]">
          <ArrowLeft className="w-3.5 h-3.5" /> BACK
        </button>

        <NextActionCard leadId={id} refreshKey={mrefresh + (lead.last_activity_at || "")} onGo={goTab} />

        <div className="border border-[#DFDAD0] border-l-4 border-l-[#3E8F99] bg-white p-5" data-testid="fit-brief-card">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Fit Brief · why this lead, what to say</div>
            <Action icon={briefBusy ? Loader2 : Sparkles} label={briefBusy ? `Generating… ${briefElapsed}s` : lead.fit_brief ? "Regenerate" : "Generate"} onClick={() => brief(false)} testid="generate-brief-btn" disabled={busy || briefBusy} spin={briefBusy} />
          </div>
          <div ref={briefRef} className={`text-sm text-[#1C2530]/80 whitespace-pre-wrap max-h-72 overflow-y-auto transition-colors ${briefFlash ? "bg-[#3E8F99]/10" : ""}`} data-testid="fit-brief-text">
            {briefBusy ? <span className="font-mono text-xs text-[#3E8F99] animate-pulse">Lead Research Agent is reading the account, contact and knowledge base…</span>
              : briefError ? <span className="font-mono text-xs text-[#B23A2E]" data-testid="fit-brief-error">{briefError}</span>
              : lead.fit_brief ? lead.fit_brief.replace(/\*\*/g, "") : <span className="font-mono text-xs text-[#1C2530]/40">NO_BRIEF · click Generate</span>}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6" id="lead-context">
          {/* Persistent context column */}
          <div className="space-y-4">
            <div className="border border-[#DFDAD0] bg-white p-5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">Lead Score</div>
              <LeadScoreGauge score={lead.total_score} band={lead.band} />
              <div className="mt-4 pt-4 border-t border-[#DFDAD0]">
                <Bar label="Fit" value={lead.fit_score} max={40} color="#3E8F99" />
                <Bar label="Engagement" value={lead.engagement_score} max={40} color="#E2711D" />
                <Bar label="Intent" value={lead.intent_score} max={20} color="#B23A2E" />
                <div className="font-mono text-[10px] text-[#1C2530]/50 mt-2">Intent label: {lead.intent}</div>
              </div>
            </div>

            <div className="border border-[#DFDAD0] bg-white p-5 space-y-3 text-sm">
              <div><div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Contact</div><div className="text-[#1C2530] font-medium mt-1">{lead.contact?.name}</div><div className="text-xs text-[#1C2530]/60">{lead.contact?.title} · {lead.contact?.seniority}</div></div>
              <CompanyIntel accountId={lead.account_id} />
              <div><div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Email</div><div className="font-mono text-xs text-[#1C2530]/80 mt-1 break-all">{lead.contact?.email || "—"}</div></div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Assigned To</div><div className="text-[#1C2530] mt-1">{lead.assignee_name || "Unassigned"}</div></div>
                <div><div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Source</div><div className="text-xs text-[#1C2530]/80 mt-1">{lead.contact?.source}</div></div>
              </div>
              {(() => {
                const enr = lead.contact?.enrichment_status || (lead.contact && !lead.contact.email ? "not_enriched" : null);
                if (!enr || enr === "enriched") return null;
                const warn = enr === "vendor_not_connected" || enr === "not_enriched";
                return (
                  <div data-testid="enrichment-status"
                    className={`flex items-start gap-2 px-3 py-2 text-xs border ${warn ? "border-[#E2711D]/40 bg-[#E2711D]/10 text-[#b1560f]" : "border-[#DFDAD0] bg-[#EFECE5] text-[#1C2530]/70"}`}>
                    <span className="font-mono text-[9px] uppercase tracking-wider shrink-0">Enrichment</span>
                    <span>{({
                      vendor_not_connected: "Not run — no data vendor connected. Connect Apollo/Kaspr to auto-fill email, phone & LinkedIn.",
                      not_enriched: "Not run for this lead — no email on file. Connect/verify Apollo or Kaspr, or add the email manually.",
                      phone_pending: "Pending — awaiting vendor phone reveal.",
                      phone_not_found: "Ran — no additional phone found.",
                    })[enr] || enr}</span>
                  </div>
                );
              })()}
              <div className="pt-3 border-t border-[#DFDAD0]" data-testid="reach-out-row">
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">One-tap deep links</div>
                <div className="flex flex-wrap gap-2">
                  <DeepLink href={`tel:${c.phone}`} disabled={!c.phone} icon={Phone} label="Call" testid="deeplink-call" />
                  <DeepLink href={`https://wa.me/${waNum}`} disabled={!waNum} icon={MessageCircle} label="WhatsApp" testid="deeplink-whatsapp" />
                  <DeepLink href={`mailto:${c.email}`} disabled={!c.email} icon={Mail} label="Email" testid="deeplink-email" />
                  <DeepLink href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(c.email || "")}`} disabled={!c.email} icon={MessageCircle} label="Teams" testid="deeplink-teams" />
                  <DeepLink href={liUrl} disabled={false} icon={Linkedin} label="LinkedIn" testid="deeplink-linkedin" />
                </div>
              </div>
            </div>

            <div className="border border-[#DFDAD0] bg-white p-5 space-y-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Stage · current: {lead.stage}</div>
              <div className="flex flex-wrap gap-1.5">
                {STAGES.map((s) => (
                  <button key={s} onClick={() => setStage(s)} disabled={busy || s === lead.stage} data-testid={`stage-btn-${s.replace(/ /g, "-")}`}
                    className={`px-2 py-1 font-mono text-[10px] uppercase border transition-colors ${s === lead.stage ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530]/60 hover:bg-[#EFECE5]"}`}>
                    {s}
                  </button>
                ))}
              </div>
              {lead.stage_aging?.applies && (
                <div className="pt-3 border-t border-[#DFDAD0]" data-testid="stage-aging">
                  <div className="flex items-center justify-between gap-2">
                    <div className={`font-mono text-[10px] uppercase ${lead.stage_aging.overdue ? "text-[#E50914]" : "text-[#1C2530]/60"}`} data-testid="stage-aging-text">
                      {lead.stage_aging.overdue ? `Overdue by ${lead.stage_aging.days_over}d` : `${lead.stage_aging.days_left}d left in stage`}
                      {lead.stage_aging.extensions > 0 && ` · ${lead.stage_aging.extensions} ext.`}
                    </div>
                    {!lead.stage_aging.hard_cap
                      ? <button onClick={extendStage} disabled={busy} data-testid="extend-stage-btn" className="font-mono text-[10px] uppercase border border-[#DFDAD0] px-3 py-1.5 hover:bg-[#EFECE5]">Extend</button>
                      : <span className="font-mono text-[9px] uppercase text-[#E50914]" data-testid="stage-hardcap">Hard cap · force Won/Lost/Nurture</span>}
                  </div>
                </div>
              )}
              <CloseoutPanel lead={lead} user={user} onChange={bump} />
              {(lead.status === "lost" || lead.status === "dnc") && !lead.closeout && (
                <div className="pt-3 border-t border-[#DFDAD0]"><Action icon={RotateCcw} label="Reinstate" onClick={reinstate} testid="reinstate-btn" disabled={busy} /></div>
              )}
              {!["lost", "dnc", "won"].includes(lead.status) && (
                <div className="pt-3 border-t border-[#DFDAD0]">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">Nurture · check back later (≠ Lost)</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="date" value={nurtureDate} onChange={(e) => setNurtureDate(e.target.value)} data-testid="nurture-date-input"
                      className="border border-[#DFDAD0] px-2 py-1.5 text-sm font-mono outline-none focus:border-[#1C2530]" />
                    <Action icon={CalendarClock} label="Move to Nurture" onClick={setNurtureFn} testid="nurture-btn" disabled={busy} />
                    {lead.status === "nurture" && lead.nurture_date && (
                      <span className="font-mono text-[10px] text-[#3E8F99] self-center" data-testid="nurture-status">re-engage {lead.nurture_date}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Work this lead — tabbed */}
          <div className="min-w-0" id="work-area">
            <div className="flex flex-wrap border-b border-[#DFDAD0] bg-white" data-testid="work-tabs">
              {TABS.map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`work-tab-${k}`}
                  className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-[#E50914] text-[#1C2530] font-medium" : "border-transparent text-[#1C2530]/55 hover:text-[#1C2530]"}`}>{l}</button>
              ))}
            </div>
            <div className="pt-5 space-y-6" data-testid={`work-panel-${tab}`}>
              {tab === "reachout" && (<>
                <FirstOutreachCard leadId={id} lead={lead} onChange={bump} />
                <WhatsAppPanel leadId={id} contact={lead.contact} region={lead.region} onChange={load} />
                <VoicePanel leadId={id} region={lead.region} onChange={bump} />
              </>)}
              {tab === "meetings" && (<>
                <MeetingBookingPanel leadId={id} region={lead.region} onBooked={bump} />
                <MeetingsPanel leadId={id} refreshKey={mrefresh} />
              </>)}
              {tab === "proposal" && <ProposalPanel leadId={id} region={lead.region} onChange={load} />}
              {tab === "timeline" && (
                <div className="border border-[#DFDAD0] bg-white p-5 space-y-5">
                  {suggested?.applies && (
                    <div className="border border-[#3E8F99]/40 bg-[#3E8F99]/5 p-4" data-testid="suggested-contacts">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-[#3E8F99]">Suggested Contacts · higher seniority / budget holders</div>
                        <span className="font-mono text-[9px] uppercase text-[#1C2530]/40">via {suggested.source}</span>
                      </div>
                      {suggested.note && <div className="text-xs text-[#E2711D] mb-2">{suggested.note}</div>}
                      {suggested.suggestions.length === 0 ? (
                        <div className="font-mono text-xs text-[#1C2530]/40" data-testid="suggested-empty">No higher-seniority contacts found for {suggested.company}.</div>
                      ) : (
                        <div className="space-y-2">
                          {suggested.suggestions.map((s, i) => (
                            <div key={i} className="flex items-center justify-between border-b border-[#DFDAD0] last:border-0 pb-2 last:pb-0" data-testid={`suggested-row-${i}`}>
                              <div>
                                <div className="text-sm text-[#1C2530] font-medium">{s.name}</div>
                                <div className="text-xs text-[#1C2530]/60">{s.title || "—"}{s.seniority ? ` · ${s.seniority}` : ""} · <span className="text-[#1C2530]/40">{s.reason}</span></div>
                              </div>
                              <a href={s.linkedin_url} target="_blank" rel="noreferrer" data-testid={`suggested-linkedin-${i}`}
                                className="flex items-center gap-1 border border-[#3E8F99] text-[#3E8F99] px-2 py-1 font-mono text-[9px] uppercase hover:bg-[#3E8F99]/10">LinkedIn</a>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 font-mono text-[9px] uppercase text-[#1C2530]/40">Suggestions only — never auto-contacted</div>
                    </div>
                  )}
                  <LifecycleTimeline leadId={id} refreshKey={mrefresh + (lead.last_activity_at || "")} header={
                    <div className="flex flex-wrap gap-2" data-testid="activity-timeline">
                      <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" placeholder="Add a note…"
                        className="border border-[#DFDAD0] px-3 py-1.5 text-sm outline-none focus:border-[#1C2530] w-64" />
                      <button onClick={addNote} disabled={!note.trim()} data-testid="add-note-btn" className="bg-[#1C2530] text-[#F5F3EE] px-3 text-xs font-mono uppercase disabled:opacity-40">Log</button>
                      <VoiceMemo leadId={id} onLogged={bump} />
                    </div>
                  } />
                </div>
              )}
              {tab === "admin" && user.role === "admin" && (
                <div className="border border-[#DFDAD0] bg-white p-5 space-y-4">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Simulate Engagement (feeds Score) · Admin/dev only</div>
                  <div className="flex flex-wrap gap-2">
                    <Action icon={Mail} label={`Open (${ev.opens || 0})`} onClick={() => engagement("open")} testid="eng-open" disabled={busy} />
                    <Action icon={Zap} label="Click" onClick={() => engagement("click")} testid="eng-click" disabled={busy} />
                    <Action icon={MessageCircle} label="LinkedIn Accept" onClick={() => engagement("linkedin_accept")} testid="eng-linkedin" disabled={busy} />
                    <Action icon={Send} label="Multi-channel" onClick={() => engagement("multichannel")} testid="eng-multichannel" disabled={busy} />
                  </div>
                  <div className="pt-4 border-t border-[#DFDAD0]">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-[#B23A2E]/80 mb-2">Danger</div>
                    <button onClick={deleteLead} disabled={busy} data-testid="delete-lead-btn"
                      className="flex items-center gap-2 border border-[#B23A2E]/40 text-[#B23A2E] px-3 py-2 text-xs font-mono uppercase tracking-wider hover:bg-[#B23A2E]/10 disabled:opacity-40">
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete lead
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
